import { useCallback, useState, useMemo, useEffect } from 'react';
import { BacklogAiWorkCreationPanelEntryPoint } from '@atlassian/jira-software-backlog-ai-work-creation/src/view/ai-panel/entrypoint.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type { AiWorkCreatePanelEntryPointSubject } from '../types.tsx';
import type { CardType } from '../../../../../model/types.tsx';

type Props = {
	selectedIssueType: CardType;
	sourceLink?: string;
	onSetAiWorkCreatePanelSubject?: (subject: AiWorkCreatePanelEntryPointSubject) => void;
	onAllSuggestionsActioned?: () => void;
	cardListId: string;
};

let previousPanelCardListId: string | undefined;
let cleanUpPreviousPanel: (() => void) | undefined;

// used to reset the cleanup function in tests
export const resetCleanupFunction = () => {
	cleanUpPreviousPanel = undefined;
	previousPanelCardListId = undefined;
};

export const useAiWorkCreatePanelEntryPoint = ({
	selectedIssueType,
	sourceLink,
	onSetAiWorkCreatePanelSubject,
	onAllSuggestionsActioned,
	cardListId,
}: Props) => {
	// used to notify AI Panel to refetch
	const [retryTrigger, setRetryTrigger] = useState(0);
	const [source, setSource] = useState<string>();

	const entryPointParams = useMemo(
		() => ({
			selectedIssueType,
			sourceLink: source ?? sourceLink ?? '',
			retryTrigger,
			onAllSuggestionsActioned,
		}),
		[source, sourceLink, retryTrigger, selectedIssueType, onAllSuggestionsActioned],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		BacklogAiWorkCreationPanelEntryPoint,
		entryPointParams,
		undefined,
		{
			preloadMaxAge: 0, // never use preloaded entryPointParams
		},
	);
	useEffect(() => {
		if (
			sourceLink &&
			expValEquals('ai_powered_backlog_inline_create_iter2', 'isEnabled', false, false)
		) {
			entryPointActions.preload();
		}
	}, [entryPointActions, sourceLink]);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(
		() => {
			if (
				entryPointParams.sourceLink &&
				expValEquals('ai_powered_backlog_inline_create_iter2', 'isEnabled', true, false)
			) {
				onSetAiWorkCreatePanelSubject?.(entryPointReferenceSubject);
				entryPointActions.load();
			}
		},
		// Re-run on retryTrigger change is a workaround right now - ideally we want to use `entryPointActions` or `entryPointParams` here
		// But `entryPointParams` turns out not to be stable in backlog ICC (because `initialCardTypes` is not stable causing selectedIssueType to change)
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[retryTrigger],
	);

	/**
	 * @deprecated Use `loadAndShowAiWorkCreatePanelForSource` instead
	 *
	// Invoked only by iter1 flow, assuming `entryPointActions` is updated with latest source
	 */
	const loadAndShowAiWorkCreatePanel = useCallback(() => {
		onSetAiWorkCreatePanelSubject?.(entryPointReferenceSubject);
		entryPointActions.load();
		setRetryTrigger((prev) => prev + 1);
	}, [entryPointActions, entryPointReferenceSubject, onSetAiWorkCreatePanelSubject]);

	// Invoked only by iter2 flow, load panel with provided source
	// No need to use sourceLink
	const loadAndShowAiWorkCreatePanelForSource = useCallback(
		(loadSource: string) => {
			if (previousPanelCardListId !== cardListId) {
				cleanUpPreviousPanel?.();
			}

			setSource(loadSource);
			setRetryTrigger((prev) => prev + 1);

			cleanUpPreviousPanel = entryPointActions.unload;
			previousPanelCardListId = cardListId;
		},
		[entryPointActions, cardListId],
	);

	return {
		unloadAiWorkCreatePanel: entryPointActions.unload,
		preloadAiWorkCreatePanel: entryPointActions.preload,
		loadAndShowAiWorkCreatePanel,
		loadAndShowAiWorkCreatePanelForSource,
	};
};
